/**
 * Endpoints de resumen operativo (módulo dashboard de EDSuite API).
 */

import { apiClient } from '@/lib/http/api-client';

export interface DateRange {
  from: string;
  to: string;
}

export interface DashboardSummary {
  total_monto: number;
  total_galones: number;
  total_transacciones: number;
  /** Comparativa contra el período anterior (devuelta por `dashboard_summary`). */
  comparacion?: DashboardComparacion;
}

/** Comparación de un indicador contra el período anterior. */
export interface DashboardComparacion {
  periodo_anterior: DateRange;
  monto: { actual: number; anterior: number; variacion: number };
  galones: { actual: number; anterior: number; variacion: number };
  transacciones: { actual: number; anterior: number; variacion: number };
}

/** Créditos por canjear: ventas a crédito sin CPE emitido. */
export interface CreditosCanjear {
  monto: number;
  cantidad: number;
}

export interface DailySale {
  fecha: string;
  monto: number;
  galones: number;
  transacciones: number;
}

/** Ventas por producto (combustible) del rango. */
export interface FuelSale {
  nombre: string;
  producto_id: number;
  galones: number;
  monto: number;
  transacciones: number;
  color: string;
}

/** Venta por método de pago (según el documento de venta). */
export interface PaymentMethodSale {
  metodo: string;
  monto: number;
  cantidad: number;
}

export type TankState = 'critico' | 'bajo' | 'normal';

/** Nivel actual de un tanque (última medición de PTS). */
export interface TankStatus {
  id_tank: number;
  description_tank: string;
  producto: string;
  capacidad_maxima: number;
  capacidad_minima: number;
  nivel_actual: number;
  nivel_logico: number;
  diferencia: number;
  porcentaje: number;
  ultima_medicion: string | null;
  estado: TankState;
}

/** Un punto por hora del día (0-23). */
export interface HourlyTrafficPoint {
  hora: number;
  hora_label: string;
  monto: number;
  galones: number;
  transacciones: number;
  ticket_promedio: number;
}

/** Rendimiento por pistola de cada dispensador. */
export interface DispenserRow {
  dispensador: number | string;
  pistola: number | string;
  combustible: string;
  monto: number;
  galones: number;
  transacciones: number;
  galones_promedio: number;
}

/** Precio promedio diario por combustible. */
export interface PriceHistoryPoint {
  fecha: string;
  combustible: string;
  combustible_id: number;
  precio_promedio: number;
  precio_minimo: number;
  precio_maximo: number;
  color: string;
}

export type DashboardAlertTone = 'danger' | 'warning' | 'info';

/** Alerta operativa (tanques y comprobantes rechazados). */
export interface DashboardAlert {
  tipo: DashboardAlertTone;
  icono: string;
  titulo: string;
  mensaje: string;
}

/** Compras, gastos y efectivo pendiente de depositar del rango. */
export interface FinancialSummary {
  compras: {
    total_compras: number;
    monto_total: number;
    proveedores: number;
    pendiente_pago: number;
  };
  gastos: {
    total_gastos: number;
    gastos_operativos: number;
    gastos_administrativos: number;
  };
  efectivo_pendiente: {
    total_pendiente: number;
    registros: number;
  };
}

/** Cliente con saldo pendiente de pago. */
export interface TopDebtor {
  razon_social: string;
  ruc: string | null;
  saldo_pendiente: number;
  documentos_pendientes: number;
}

export interface TopDebtors {
  saldo_total_por_cobrar: number;
  detalle: TopDebtor[];
}

/** Turno abierto (resumen ligero para el dashboard). */
export interface ActiveShift {
  id: number;
  operador: string;
  hora_llegada: string;
  horas_transcurridas: number;
  monto_acumulado: number;
  transacciones: number;
}

/** Meta activa del módulo de metas (`pts_goals`). */
export interface ActiveGoal {
  id: number;
  goal_name: string;
  goal_type: string;
  target_value: number;
  current_value: number;
  unit_measurement: string | null;
  end_date: string;
  status: string;
  priority: string;
  fuel_grade_name: string | null;
  /** `completed` | `on_track` | `warning` | `behind` | `expired`. */
  performance_status: string;
  progress_percentage: number;
  days_remaining: number;
}

export interface GoalsDashboard {
  active_goals: ActiveGoal[];
  stats?: {
    total_active?: number;
    total_completed?: number;
    /** Metas activas que vencen en 7 días o menos y van por debajo del 50 %. */
    at_risk?: number;
    /** Avance promedio (%) de las metas activas. */
    avg_completion?: number;
    [clave: string]: unknown;
  };
}

/** Claves de caché centralizadas (evita strings sueltos por la app). */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: (range: DateRange) => ['dashboard', 'summary', range.from, range.to] as const,
  dailySales: (range: DateRange) => ['dashboard', 'daily-sales', range.from, range.to] as const,
  fuelSales: (range: DateRange) => ['dashboard', 'fuel-sales', range.from, range.to] as const,
  paymentMethods: (range: DateRange) => ['dashboard', 'payment-methods', range.from, range.to] as const,
  hourlyTraffic: (range: DateRange) => ['dashboard', 'hourly-traffic', range.from, range.to] as const,
  dispenserPerformance: (range: DateRange) =>
    ['dashboard', 'dispensers', range.from, range.to] as const,
  priceHistory: (range: DateRange) => ['dashboard', 'price-history', range.from, range.to] as const,
  financialSummary: (range: DateRange) =>
    ['dashboard', 'financial-summary', range.from, range.to] as const,
  creditosCanjear: (range: DateRange) =>
    ['dashboard', 'creditos-canjear', range.from, range.to] as const,
  tanks: ['dashboard', 'tanks'] as const,
  alerts: ['dashboard', 'alerts'] as const,
  activeShifts: ['dashboard', 'active-shifts'] as const,
  topDebtors: ['dashboard', 'top-deudores'] as const,
  goals: ['dashboard', 'goals'] as const,
};

export const dashboardApi = {
  summary: (range: DateRange, signal?: AbortSignal): Promise<DashboardSummary> =>
    apiClient.get<DashboardSummary>('/api/dashboard_summary', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  dailySales: (range: DateRange, signal?: AbortSignal): Promise<DailySale[]> =>
    apiClient.get<DailySale[]>('/api/dashboard_daily_sales', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  fuelSales: (range: DateRange, signal?: AbortSignal): Promise<FuelSale[]> =>
    apiClient.get<FuelSale[]>('/api/dashboard_fuel_sales', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  paymentMethods: (range: DateRange, signal?: AbortSignal): Promise<PaymentMethodSale[]> =>
    apiClient.get<PaymentMethodSale[]>('/api/dashboard_payment_methods', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  hourlyTraffic: (range: DateRange, signal?: AbortSignal): Promise<HourlyTrafficPoint[]> =>
    apiClient.get<HourlyTrafficPoint[]>('/api/dashboard_hourly_traffic', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  dispenserPerformance: (range: DateRange, signal?: AbortSignal): Promise<DispenserRow[]> =>
    apiClient.get<DispenserRow[]>('/api/dashboard_dispenser_performance', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  priceHistory: (range: DateRange, signal?: AbortSignal): Promise<PriceHistoryPoint[]> =>
    apiClient.get<PriceHistoryPoint[]>('/api/dashboard_price_history', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  /** El estado de los tanques es el actual (no depende del rango de fechas). */
  tanks: (signal?: AbortSignal): Promise<TankStatus[]> =>
    apiClient.get<TankStatus[]>('/api/dashboard_tanks', { signal }),

  alerts: (signal?: AbortSignal): Promise<DashboardAlert[]> =>
    apiClient.get<DashboardAlert[]>('/api/dashboard_alerts', { signal }),

  financialSummary: (range: DateRange, signal?: AbortSignal): Promise<FinancialSummary> =>
    apiClient.get<FinancialSummary>('/api/dashboard_financial_summary', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  /** Créditos por canjear del rango (ventas a crédito sin CPE emitido). */
  creditosCanjear: (range: DateRange, signal?: AbortSignal): Promise<CreditosCanjear> =>
    apiClient.get<CreditosCanjear>('/api/dashboard_creditos_canjear', {
      query: { from_date: range.from, to_date: range.to },
      signal,
    }),

  /** Top de clientes con saldo por cobrar (el máximo del API es 30). */
  topDebtors: (signal?: AbortSignal, limite = 8): Promise<TopDebtors> =>
    apiClient.get<TopDebtors>('/api/dashboard_top_deudores', {
      query: { limit: limite },
      signal,
    }),

  activeShifts: (signal?: AbortSignal): Promise<ActiveShift[]> =>
    apiClient.get<ActiveShift[]>('/api/dashboard_active_shifts', { signal }),

  /** Metas del módulo de metas (`/api/goals/dashboard`): sólo se usan las activas. */
  goals: (signal?: AbortSignal): Promise<GoalsDashboard> =>
    apiClient.get<GoalsDashboard>('/api/goals/dashboard', { signal }),
} as const;
